import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { 
  LayoutDashboard, 
  Menu, 
  Bell, 
  LogOut, 
  Settings, 
  ChevronRight,
  X,
  HardHat,
  User,
  MapPin,
  IndianRupee,
  PanelLeftClose,
  PanelLeft,
  HandCoins,
  BookOpen
} from "lucide-react";
import { supabase } from "../lib/supabase";
import { toast } from "react-hot-toast";

const navItems = [
  { name: "Project Overview", path: "/", icon: <LayoutDashboard size={22} /> },
  { name: "Site Inventory", path: "/sites", icon: <MapPin size={22} /> },
  { name: "Supervisor", path: "/supervisor", icon: <User size={22} /> },
  { name: "Personal Expenses", path: "/personal-expenses", icon: <IndianRupee size={22} /> },
  { name: "Daily Logs", path: "/daily-logs", icon: <LayoutDashboard size={22} /> },
  { name: "Invoice Management", path: "/invoices", icon: <IndianRupee size={22} /> },
  { name: "MB", path: "/mb", icon: <IndianRupee size={22} /> },
  { name: "Credit Section", path: "/credit", icon: <HandCoins size={22} /> }, 
  { name: "Blogs", path: "/blogs", icon: <BookOpen size={22} /> },
  { name: "Portal Settings", path: "/settings", icon: <Settings size={22} /> }
];

export default function Layout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  
  const [businessName, setBusinessName] = useState("Shree Construction");
  const [adminDisplayName, setAdminDisplayName] = useState("");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false); 

  useEffect(() => {
    fetchBusinessSettings();
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const fetchBusinessSettings = async () => {
    try {
      const { data } = await supabase.from("settings").select("shop_name, admin_name").maybeSingle();
      if (data) {
        setBusinessName(data.shop_name);
        setAdminDisplayName(data.admin_name);
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
    }
  };

  const handleLogout = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      toast.error("Logout failed");
    } else {
      toast.success("Signed out successfully");
      navigate("/login");
    }
  };

  const SidebarContent = ({ isMobile = false }) => (
    <>
      {/* Header & Toggle */}
      <div className={`p-6 flex items-center justify-between ${isCollapsed && !isMobile ? "justify-center" : ""}`}>
        <div className={`flex items-center gap-3 transition-opacity duration-300 ${isCollapsed && !isMobile ? "hidden" : "opacity-100"}`}>
          <div className="h-10 w-10 bg-[#0054A6] rounded-xl flex items-center justify-center text-white shadow-lg shadow-blue-100 shrink-0">
            <HardHat size={22} />
          </div>
          <div className="flex flex-col whitespace-nowrap overflow-hidden">
            <span className="text-sm font-black text-slate-800 uppercase tracking-tight">{businessName}</span>
            <span className="text-[10px] font-bold text-[#EE3124] tracking-widest mt-1">Lucknow</span>
          </div>
        </div>
        
        {/* Collapse Toggle (Desktop only) */}
        {!isMobile && (
          <button 
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="p-2 text-slate-400 hover:bg-slate-100 rounded-lg transition-colors"
          >
            {isCollapsed ? <PanelLeft size={20} /> : <PanelLeftClose size={20} />}
          </button>
        )}
      </div>
      
      {/* Navigation */}
      <nav className={`flex-1 px-4 space-y-2 mt-4 overflow-y-auto overflow-x-hidden`}>
        <p className={`px-4 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4 transition-all ${isCollapsed && !isMobile ? "opacity-0 h-0" : "opacity-100"}`}>
          Operations
        </p>
        
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.name}
              to={item.path}
              title={isCollapsed ? item.name : ""}
              className={`flex items-center px-4 py-3 rounded-xl transition-all group relative ${
                isActive 
                  ? "bg-[#0054A6] text-white shadow-lg shadow-blue-200" 
                  : "text-slate-500 hover:bg-blue-50 hover:text-[#0054A6]"
              } ${isCollapsed && !isMobile ? "justify-center" : ""}`}
            >
              <div className="flex items-center gap-4 shrink-0">
                <span className={`${isActive ? "text-white" : "text-slate-400 group-hover:text-[#0054A6]"} transition-colors`}>
                  {item.icon}
                </span>
                <span className={`font-bold text-sm whitespace-nowrap transition-all duration-300 ${isCollapsed && !isMobile ? "opacity-0 w-0 translate-x-10" : "opacity-100 w-auto translate-x-0"}`}>
                  {item.name}
                </span>
              </div>
              
              {!isCollapsed || isMobile ? (
                isActive && <ChevronRight size={14} className="opacity-50 ml-auto" />
              ) : null}
            </Link>
          );
        })}
      </nav>

      {/* Logout */}
      <div className={`px-4 pb-6 mt-auto ${isCollapsed && !isMobile ? "flex justify-center" : ""}`}>
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-4 px-4 py-3 rounded-xl text-slate-400 hover:bg-red-50 hover:text-red-600 transition-all font-bold text-sm"
        >
          <LogOut size={20} className="shrink-0" />
          <span className={`transition-all duration-300 ${isCollapsed && !isMobile ? "opacity-0 w-0" : "opacity-100 w-auto"}`}>Sign Out</span>
        </button>
      </div>
    </>
  );

  return (
    <div className="flex h-screen bg-slate-50 font-sans text-slate-900 overflow-hidden">
      {/* --- Desktop Sidebar --- */}
      <aside 
        className={`bg-white hidden md:flex flex-col border-r border-slate-200 shadow-sm transition-all duration-500 ease-in-out ${
          isCollapsed ? "w-24" : "w-72"
        }`}
      >
        <SidebarContent />
      </aside>

      {/* --- Mobile Sidebar Drawer --- */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={() => setIsMobileMenuOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-72 bg-white flex flex-col shadow-2xl animate-in slide-in-from-left duration-300">
            <div className="absolute top-4 right-4">
              <button onClick={() => setIsMobileMenuOpen(false)} className="p-2 text-slate-400"><X size={24} /></button>
            </div>
            <SidebarContent isMobile={true} />
          </aside>
        </div>
      )}

      {/* --- Main Content Area --- */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 lg:h-20 bg-white/90 backdrop-blur-md border-b border-slate-200 flex items-center justify-between px-4 lg:px-10 z-10">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => setIsMobileMenuOpen(true)}
              className="md:hidden p-2 text-slate-600 hover:bg-slate-100 rounded-lg"
            >
              <Menu size={24} />
            </button>
            
            <div className="flex flex-col overflow-hidden">
              <h1 className="text-xs lg:text-sm font-black text-slate-900 uppercase truncate">
                {location.pathname === "/" ? "Site Monitoring" : location.pathname.replace('/', '').replace(/-/g, ' ')}
              </h1>
              <p className="hidden xs:block text-[10px] text-slate-400 font-bold uppercase tracking-tighter">
                HPCL Contractor Management
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3 lg:gap-6">
            <button className="p-2 text-slate-400 hover:bg-blue-50 hover:text-[#0054A6] rounded-lg transition-all relative">
                <Bell size={20} />
                <span className="absolute top-2.5 right-2.5 h-2 w-2 bg-[#EE3124] rounded-full border-2 border-white"></span>
            </button>
            
            <div className="flex items-center gap-3 pl-3 lg:pl-6 border-l border-slate-200">
              <div className="text-right hidden sm:block">
                <p className="text-xs font-black text-slate-900">{adminDisplayName}</p>
                <p className="text-[10px] font-bold text-[#EE3124] uppercase tracking-tighter">Vendor Admin</p>
              </div>
              <div className="h-9 w-9 lg:h-11 lg:w-11 rounded-xl bg-blue-50 flex items-center justify-center overflow-hidden shadow-sm border border-blue-100 transition-transform active:scale-95 cursor-pointer">
                <img src="/images/aham.jpeg" alt="Profile" className="h-full w-full object-cover" />
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto bg-slate-50">
          <div className="max-w-[1600px] mx-auto">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}